import { useState, useEffect } from "react";
import {
  Form,
  Button,
  Spinner,
  Row,
  Col,
  Card,
  InputGroup,
  Alert,
} from "react-bootstrap";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useCreateUserMutation } from "../../../../../api/jeep-rally/users/users.hooks";

const JeepRallyUserForm = ({
  onCancel,
  onSave,
  user = null,
  mode = "create",
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    cnic: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const {
    mutateAsync: createJeepRallyUser,
    isPending: isCreating,
    error: createError,
  } = useCreateUserMutation();

  const isLoading = isCreating;
  const error = createError;

  useEffect(() => {
    if (user && (mode === "edit" || mode === "view")) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        cnic: user.cnic || "",
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        cnic: "",
      });
    }
    setValidationErrors({});
  }, [user, mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (mode === "create" && !formData.password.trim()) {
      errors.password = "Password is required";
    } else if (mode === "create" && formData.password.trim().length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.cnic.trim()) {
      errors.cnic = "CNIC is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (mode === "view" || mode === "edit") {
      onCancel();
      return;
    }

    if (!validateForm()) return;

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: "admin",
        cnic: formData.cnic.trim(),
      };

      await createJeepRallyUser(payload);
      onSave && onSave(payload);
    } catch (err) {
      console.error("Jeep Rally user mutation error:", err);
    }
  };

  const getFormTitle = () => {
    switch (mode) {
      case "create":
        return "Add New Jeep Rally User";
      case "edit":
        return "Edit Jeep Rally User";
      case "view":
        return "View Jeep Rally User Details";
      default:
        return "Jeep Rally User";
    }
  };

  const isReadOnly = mode === "view" || mode === "edit";

  return (
    <Card className="p-4">
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={onCancel}>
          ← Back
        </Button>
        <h4 className="m-0">{getFormTitle()}</h4>
      </div>

      {mode === "edit" && (
        <Alert variant="info">
          Updating Jeep Rally users is not available yet. You can view details only.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible>
          {typeof error === "string"
            ? error
            : error?.response?.data?.message ||
              error?.message ||
              "An error occurred"}
        </Alert>
      )}

      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Name {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                isInvalid={!!validationErrors.name}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Email {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                isInvalid={!!validationErrors.email}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Password{" "}
                {!isReadOnly && mode === "create" && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  isInvalid={!!validationErrors.password}
                  disabled={isReadOnly}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isReadOnly}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.password}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                CNIC {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter CNIC"
                value={formData.cnic}
                onChange={(e) => handleInputChange("cnic", e.target.value)}
                isInvalid={!!validationErrors.cnic}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.cnic}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Control type="text" value="admin" disabled />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Module</Form.Label>
              <Form.Control type="text" value="Jeep Rally" disabled />
              <Form.Text className="text-muted">
                This form is for Jeep Rally users only
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            {mode === "view" || mode === "edit" ? "Close" : "Cancel"}
          </Button>
          {mode === "create" && (
            <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Adding...
                </>
              ) : (
                "Add User"
              )}
            </Button>
          )}
        </div>
      </Form>
    </Card>
  );
};

export default JeepRallyUserForm;
